import { pgTable, text, serial, integer, boolean, timestamp, real, jsonb, date, varchar, index, decimal, doublePrecision } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { relations } from "drizzle-orm";

// User roles enum
export enum UserRole {
  GUEST = "guest",
  AGENT = "agent",
  MANAGER = "manager",
  OWNER = "owner",
  ADMIN = "admin"
}

// User status enum
export enum UserStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  INACTIVE = "inactive"
}

// Property status enum
export enum PropertyStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
  REJECTED = "rejected"
}

// Booking status enum
export enum BookingStatus {
  TEMPORARY = "temporary", // Temporary booking waiting for payment
  PENDING = "pending",
  CONFIRMED = "confirmed",
  PAID = "paid",
  COMPLETED = "completed",
  CANCELLED = "cancelled"
}

// Custom offer status enum
export enum CustomOfferStatus {
  ACTIVE = "active",
  VIEWED = "viewed",
  LINK_CREATED = "link_created", 
  BOOKED = "booked",
  EXPIRED = "expired",
  REVOKED = "revoked"
}

// Users table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  email: text("email").notNull().unique(),
  fullName: text("full_name").notNull(),
  phone: text("phone"),
  role: text("role").$type<UserRole>().notNull().default(UserRole.GUEST),
  status: text("status").$type<UserStatus>().notNull().default(UserStatus.PENDING),
  stripeAccountId: text("stripe_account_id"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  globalMarkupPercentage: real("global_markup_percentage"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Additional fields for property manager
  companyName: text("company_name"),
  country: text("country"),
  idDocumentUrl: text("id_document_url"),
  verificationNotes: text("verification_notes"),
  rejectionReason: text("rejection_reason"),
  googleId: text("google_id"),
  // API access fields
  apiKey: text("api_key").unique(), // API key for external system access
  apiKeyExpiresAt: timestamp("api_key_expires_at"), // When API key expires
  // Agent payout fields
  accountName: text("account_name"),
  bankName: text("bank_name"),
  ibanNumber: text("iban_number"),
  swiftCode: text("swift_code"),
  totalEarnings: integer("total_earnings").default(0),
  pendingEarnings: integer("pending_earnings").default(0),
  paidEarnings: integer("paid_earnings").default(0),
  lastPayout: timestamp("last_payout"),
});

// Properties table
export const properties = pgTable("properties", {
  id: serial("id").primaryKey(),
  ownerId: integer("owner_id").notNull(), // Field renamed to managerId in code but DB column remains "owner_id"
  title: text("title").notNull(),
  description: text("description").notNull(),
  area: text("area").notNull(),
  address: text("address").notNull(),
  bedrooms: integer("bedrooms").notNull(),
  bathrooms: decimal("bathrooms", { precision: 3, scale: 1 }).notNull(),
  maxGuests: integer("max_guests").notNull(),
  basePrice: decimal("base_price").notNull(), // in cents
  features: text("features").array(),
  status: text("status").$type<PropertyStatus>().notNull().default(PropertyStatus.PENDING),
  iCalUrl: text("ical_url"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Additional fields for Airbnb integration
  sourceUrl: text("source_url"), // Original Airbnb URL if imported
  airbnbListingId: text("airbnb_listing_id"), // Airbnb listing ID if imported
  airbnbTitle: text("airbnb_title"), // Airbnb title if imported
  rejectionReason: text("rejection_reason"), // If admin rejects the property
  amenities: jsonb("amenities"), // Detailed amenities as JSON
  lastSyncedAt: timestamp("last_synced_at"), // For iCal sync tracking
  // Smart AI Pricing fields
  useSmartPricing: boolean("use_smart_pricing").default(false), // Toggle for Smart AI Pricing
  lastAiPriceUpdate: timestamp("last_ai_price_update"), // When AI last generated prices
  // Location and rating fields
  lat: doublePrecision("lat"),
  lng: doublePrecision("lng"),
  locationTitle: text("location_title"),
  locationDescription: text("location_description"),
  rating: text("rating"),
  reviewCount: integer("review_count"),
  featured: boolean("featured").default(false),
});

// Property images table
export const propertyImages = pgTable("property_images", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull(),
  imageUrl: text("image_url").notNull(),
  isPrimary: boolean("is_primary").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Agent markup table
export const agentMarkups = pgTable("agent_markups", {
  id: serial("id").primaryKey(),
  agentId: integer("agent_id").notNull(),
  propertyId: integer("property_id").notNull(),
  markupPercentage: real("markup_percentage").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Agent links table
export const agentLinks = pgTable("agent_links", {
  id: serial("id").primaryKey(),
  agentId: integer("agent_id").notNull(),
  propertyId: integer("property_id").notNull(),
  linkCode: text("link_code").notNull().unique(),
  markupPercentage: real("markup_percentage").notNull(),
  allProperties: boolean("all_properties").default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Bookings table
export const bookings = pgTable("bookings", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull(),
  guestId: integer("guest_id"),
  agentId: integer("agent_id"),
  agentLinkId: integer("agent_link_id"),
  checkIn: timestamp("check_in").notNull(),
  checkOut: timestamp("check_out").notNull(),
  guestCount: integer("guest_count").notNull(),
  baseAmount: integer("base_amount").notNull(), // in cents
  markupAmount: integer("markup_amount").notNull(), // in cents
  platformFeeAmount: integer("platform_fee_amount").notNull(), // in cents
  totalAmount: integer("total_amount").notNull(), // in cents
  baseAmountInFils: integer("base_amount_in_fils").notNull(), // in fils
  markupAmountInFils: integer("markup_amount_in_fils").notNull(), // in fils
  platformFeeAmountInFils: integer("platform_fee_amount_in_fils").notNull(), // in fils
  totalAmountInFils: integer("total_amount_in_fils").notNull(), // in fils
  guestName: text("guest_name").notNull(),
  guestEmail: text("guest_email").notNull(),
  guestPhone: text("guest_phone"),
  specialRequests: text("special_requests"),
  status: text("status").$type<BookingStatus>().notNull().default(BookingStatus.PENDING),
  stripePaymentIntentId: text("stripe_payment_intent_id"),
  expiresAt: timestamp("expires_at"), // When temporary booking expires (15 minutes from creation)
  // Payout tracking fields
  payoutStatus: text("payout_status").default('pending'), // 'pending', 'completed'
  payoutScheduledDate: timestamp("payout_scheduled_date"), // When the payout is scheduled (usually check-in date)
  payoutCompletedDate: timestamp("payout_completed_date"), // When the payout was actually completed
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Calendar date status enum
export enum CalendarDateStatus {
  AVAILABLE = "available",
  BOOKED = "booked",
  BLOCKED = "blocked"
}

// Calendar source enum
export enum CalendarSource {
  MANUAL = "manual",
  ICAL = "ical",
  AIRBNB = "airbnb",
  BOOKING = "booking",
  GUESTY = "guesty",
  HOSTAWAY = "hostaway"
}

// Add a new calendar source enum value for AI
export enum CalendarPriceSource {
  MANUAL = "manual",
  AI = "ai",
  PRICE_LABS = "price_labs"
}

// Property calendar table - for availability and pricing by date
export const propertyCalendar = pgTable("property_calendar", {
  id: serial("id").primaryKey(),
  propertyId: integer("property_id").notNull(),
  date: date("date").notNull(),
  status: text("status").$type<CalendarDateStatus>().notNull().default(CalendarDateStatus.AVAILABLE),
  price: integer("price"), // If null, use property's basePrice
  source: text("source").$type<CalendarSource>().notNull().default(CalendarSource.MANUAL),
  sourceEventId: text("source_event_id"), // To track external event IDs for syncing
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // Smart AI Pricing fields
  aiPrice: integer("ai_price"), // AI-generated price for this date
  customPrice: integer("custom_price"), // Manual override price for this date
  priceSource: text("price_source").$type<CalendarPriceSource>().default(CalendarPriceSource.MANUAL), // Where the final price came from
  aiConfidence: real("ai_confidence"), // Confidence score from 0.0 to 1.0
  // Airbnb-specific fields for better UI logic
  available: boolean("available"), // Raw Airbnb available field
  availableForCheckin: boolean("available_for_checkin"), // Can guests check in on this date
  availableForCheckout: boolean("available_for_checkout"), // Can guests check out on this date
  bookable: boolean("bookable"), // Is the date bookable on Airbnb
  maxNights: integer("max_nights"), // Maximum nights from this date (Airbnb constraint)
  minNights: integer("min_nights"), // Minimum nights from this date (Airbnb constraint)
});

// Custom offers table
export const customOffers = pgTable("custom_offers", {
  id: serial("id").primaryKey(),
  senderId: integer("sender_id").notNull(), // Property manager/owner who created the offer
  recipientId: integer("recipient_id").notNull(), // User who receives the offer (agent/manager)
  propertyId: integer("property_id").notNull(),
  specialPrice: integer("special_price").notNull(), // Special nightly price in AED
  availabilityStart: date("availability_start").notNull(), 
  availabilityEnd: date("availability_end").notNull(),
  notes: text("notes"),
  status: text("status").$type<CustomOfferStatus>().notNull().default(CustomOfferStatus.ACTIVE),
  offerLinkCode: text("offer_link_code").unique(), // Generated when recipient creates a link
  createdAt: timestamp("created_at").defaultNow().notNull(),
  lastViewedAt: timestamp("last_viewed_at"),
  expiresAt: timestamp("expires_at"), // Optional expiration date
  revokedAt: timestamp("revoked_at"), // Timestamp when offer was revoked
});

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true
});

export const insertPropertySchema = createInsertSchema(properties).omit({
  id: true,
  createdAt: true
});

export const insertPropertyImageSchema = createInsertSchema(propertyImages).omit({
  id: true,
  createdAt: true
});

export const insertAgentMarkupSchema = createInsertSchema(agentMarkups).omit({
  id: true,
  createdAt: true
});

export const insertAgentLinkSchema = createInsertSchema(agentLinks).omit({
  id: true,
  createdAt: true
});

export const insertCalendarDateSchema = createInsertSchema(propertyCalendar).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

// Create base insertBookingSchema and then modify it to accept string dates
const baseInsertBookingSchema = createInsertSchema(bookings).omit({
  id: true,
  createdAt: true,
  status: true,
  stripePaymentIntentId: true,
  expiresAt: true
});

// Override checkIn and checkOut to accept strings that will be converted to Date objects
export const insertBookingSchema = baseInsertBookingSchema.extend({
  checkIn: z.string().transform(str => new Date(str)),
  checkOut: z.string().transform(str => new Date(str)),
  status: z.nativeEnum(BookingStatus).optional(),
  expiresAt: z.date().optional()
});

export const insertPropertyCalendarSchema = createInsertSchema(propertyCalendar).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

// Create base insertCustomOfferSchema and then modify it to accept string dates
const baseInsertCustomOfferSchema = createInsertSchema(customOffers).omit({
  id: true,
  createdAt: true,
  lastViewedAt: true,
  revokedAt: true,
  offerLinkCode: true
});

// Override availabilityStart and availabilityEnd to accept strings that will be converted to Date objects
export const insertCustomOfferSchema = baseInsertCustomOfferSchema.extend({
  availabilityStart: z.string().transform(str => new Date(str)),
  availabilityEnd: z.string().transform(str => new Date(str))
});

// Inquiry schema (not stored in database, just for validation)
export const inquirySchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  phone: z.string().min(5).max(20),
  message: z.string().min(10).max(1000),
  propertyId: z.number().optional(),
  checkIn: z.string().optional(),
  checkOut: z.string().optional(),
  agentLinkId: z.number().optional(),
});

// Community feature schemas

// Community question status
export enum CommunityQuestionStatus {
  ACTIVE = "active",
  HIDDEN = "hidden",
  REPORTED = "reported"
}

// Tags table for community questions
export const communityTags = pgTable("community_tags", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 50 }).notNull().unique(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Questions table for community
export const communityQuestions = pgTable("community_questions", {
  id: serial("id").primaryKey(),
  authorId: integer("author_id").notNull(), // User ID who asked the question
  title: varchar("title", { length: 200 }).notNull(),
  content: text("content").notNull(),
  status: text("status").$type<CommunityQuestionStatus>().notNull().default(CommunityQuestionStatus.ACTIVE),
  upvotes: integer("upvotes").default(0).notNull(),
  downvotes: integer("downvotes").default(0).notNull(),
  views: integer("views").default(0).notNull(),
  isModeratorPinned: boolean("is_moderator_pinned").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => {
  return {
    authorIdIdx: index("community_questions_author_id_idx").on(table.authorId),
    titleIdx: index("community_questions_title_idx").on(table.title),
  }
});

// Question tags junction table
export const communityQuestionTags = pgTable("community_question_tags", {
  id: serial("id").primaryKey(),
  questionId: integer("question_id").notNull(),
  tagId: integer("tag_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => {
  return {
    tagQuestionIdx: index("community_question_tags_idx").on(table.questionId, table.tagId),
  }
});

// Answers table for community
export const communityAnswers = pgTable("community_answers", {
  id: serial("id").primaryKey(),
  questionId: integer("question_id").notNull(),
  authorId: integer("author_id").notNull(),
  content: text("content").notNull(),
  upvotes: integer("upvotes").default(0).notNull(),
  downvotes: integer("downvotes").default(0).notNull(),
  isAcceptedAnswer: boolean("is_accepted_answer").default(false).notNull(),
  isModeratorVerified: boolean("is_moderator_verified").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => {
  return {
    questionIdIdx: index("community_answers_question_id_idx").on(table.questionId),
    authorIdIdx: index("community_answers_author_id_idx").on(table.authorId),
  }
});

// User question follows table
export const communityQuestionFollows = pgTable("community_question_follows", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  questionId: integer("question_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => {
  return {
    userQuestionIdx: index("community_question_follows_idx").on(table.userId, table.questionId),
  }
});

// Table relationships
export const usersRelations = relations(users, ({ many }) => ({
  properties: many(properties),
  bookingsAsGuest: many(bookings, { relationName: "guest" }),
  bookingsAsAgent: many(bookings, { relationName: "agent" }),
  agentMarkups: many(agentMarkups),
  agentLinks: many(agentLinks),
  sentOffers: many(customOffers, { relationName: "sender" }),
  receivedOffers: many(customOffers, { relationName: "recipient" }),
  // Community relations
  communityQuestions: many(communityQuestions, { relationName: "questionAuthor" }),
  communityAnswers: many(communityAnswers, { relationName: "answerAuthor" }),
  communityQuestionFollows: many(communityQuestionFollows)
}));

export const propertiesRelations = relations(properties, ({ one, many }) => ({
  manager: one(users, {
    fields: [properties.ownerId],
    references: [users.id]
  }),
  images: many(propertyImages),
  bookings: many(bookings),
  agentMarkups: many(agentMarkups),
  agentLinks: many(agentLinks),
  calendarDates: many(propertyCalendar),
  customOffers: many(customOffers)
}));

export const propertyImagesRelations = relations(propertyImages, ({ one }) => ({
  property: one(properties, {
    fields: [propertyImages.propertyId],
    references: [properties.id]
  })
}));

export const agentMarkupsRelations = relations(agentMarkups, ({ one }) => ({
  agent: one(users, {
    fields: [agentMarkups.agentId],
    references: [users.id]
  }),
  property: one(properties, {
    fields: [agentMarkups.propertyId],
    references: [properties.id]
  })
}));

export const agentLinksRelations = relations(agentLinks, ({ one, many }) => ({
  agent: one(users, {
    fields: [agentLinks.agentId],
    references: [users.id]
  }),
  property: one(properties, {
    fields: [agentLinks.propertyId],
    references: [properties.id]
  }),
  bookings: many(bookings)
}));

export const bookingsRelations = relations(bookings, ({ one }) => ({
  property: one(properties, {
    fields: [bookings.propertyId],
    references: [properties.id]
  }),
  guest: one(users, {
    fields: [bookings.guestId],
    references: [users.id],
    relationName: "guest"
  }),
  agent: one(users, {
    fields: [bookings.agentId],
    references: [users.id],
    relationName: "agent"
  }),
  agentLink: one(agentLinks, {
    fields: [bookings.agentLinkId],
    references: [agentLinks.id]
  })
}));

// Property calendar relations
export const propertyCalendarRelations = relations(propertyCalendar, ({ one }) => ({
  property: one(properties, {
    fields: [propertyCalendar.propertyId],
    references: [properties.id]
  })
}));

// Custom offers relations
export const customOffersRelations = relations(customOffers, ({ one }) => ({
  sender: one(users, {
    fields: [customOffers.senderId],
    references: [users.id],
    relationName: "sender"
  }),
  recipient: one(users, {
    fields: [customOffers.recipientId],
    references: [users.id],
    relationName: "recipient"
  }),
  property: one(properties, {
    fields: [customOffers.propertyId],
    references: [properties.id]
  })
}));

// Type definitions
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type Property = typeof properties.$inferSelect & { featured?: boolean };
export type InsertProperty = z.infer<typeof insertPropertySchema> & { featured?: boolean };

export type PropertyImage = typeof propertyImages.$inferSelect;
export type InsertPropertyImage = z.infer<typeof insertPropertyImageSchema>;

export type AgentMarkup = typeof agentMarkups.$inferSelect;
export type InsertAgentMarkup = z.infer<typeof insertAgentMarkupSchema>;

export type AgentLink = typeof agentLinks.$inferSelect;
export type InsertAgentLink = z.infer<typeof insertAgentLinkSchema>;

export type Booking = typeof bookings.$inferSelect;
export type InsertBooking = z.infer<typeof insertBookingSchema>;

export type PropertyCalendar = typeof propertyCalendar.$inferSelect;
export type InsertPropertyCalendar = z.infer<typeof insertPropertyCalendarSchema>;

export type CustomOffer = typeof customOffers.$inferSelect;
export type InsertCustomOffer = z.infer<typeof insertCustomOfferSchema>;

export type Inquiry = z.infer<typeof inquirySchema>;

// Community relation definitions
export const communityQuestionsRelations = relations(communityQuestions, ({ one, many }) => ({
  author: one(users, {
    fields: [communityQuestions.authorId],
    references: [users.id],
    relationName: "questionAuthor"
  }),
  answers: many(communityAnswers),
  tags: many(communityQuestionTags),
  follows: many(communityQuestionFollows)
}));

export const communityAnswersRelations = relations(communityAnswers, ({ one }) => ({
  question: one(communityQuestions, {
    fields: [communityAnswers.questionId],
    references: [communityQuestions.id]
  }),
  author: one(users, {
    fields: [communityAnswers.authorId],
    references: [users.id],
    relationName: "answerAuthor"
  })
}));

export const communityQuestionTagsRelations = relations(communityQuestionTags, ({ one }) => ({
  question: one(communityQuestions, {
    fields: [communityQuestionTags.questionId],
    references: [communityQuestions.id]
  }),
  tag: one(communityTags, {
    fields: [communityQuestionTags.tagId],
    references: [communityTags.id]
  })
}));

export const communityQuestionFollowsRelations = relations(communityQuestionFollows, ({ one }) => ({
  user: one(users, {
    fields: [communityQuestionFollows.userId],
    references: [users.id]
  }),
  question: one(communityQuestions, {
    fields: [communityQuestionFollows.questionId],
    references: [communityQuestions.id]
  })
}));

export const communityTagsRelations = relations(communityTags, ({ many }) => ({
  questionTags: many(communityQuestionTags)
}));

// Community insert schemas
export const insertCommunityTagSchema = createInsertSchema(communityTags).omit({
  id: true,
  createdAt: true
});

export const insertCommunityQuestionSchema = z.object({
  title: z.string().min(1).max(200),
  content: z.string().min(1),
  tags: z.array(z.union([
    z.number(), // existing tag ID
    z.object({ // new tag
      name: z.string().min(1).max(50),
      description: z.string().optional()
    })
  ])).optional()
});

export const insertCommunityAnswerSchema = createInsertSchema(communityAnswers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  upvotes: true,
  downvotes: true,
  isAcceptedAnswer: true,
  isModeratorVerified: true
});

export const insertCommunityQuestionTagSchema = createInsertSchema(communityQuestionTags).omit({
  id: true,
  createdAt: true
});

export const insertCommunityQuestionFollowSchema = createInsertSchema(communityQuestionFollows).omit({
  id: true,
  createdAt: true
});

// Community types
export type CommunityTag = typeof communityTags.$inferSelect;
export type InsertCommunityTag = z.infer<typeof insertCommunityTagSchema>;

export type CommunityQuestion = typeof communityQuestions.$inferSelect;
export type InsertCommunityQuestion = z.infer<typeof insertCommunityQuestionSchema>;

export type CommunityAnswer = typeof communityAnswers.$inferSelect;
export type InsertCommunityAnswer = z.infer<typeof insertCommunityAnswerSchema>;

export type CommunityQuestionTag = typeof communityQuestionTags.$inferSelect;
export type InsertCommunityQuestionTag = z.infer<typeof insertCommunityQuestionTagSchema>;

export type CommunityQuestionFollow = typeof communityQuestionFollows.$inferSelect;
export type InsertCommunityQuestionFollow = z.infer<typeof insertCommunityQuestionFollowSchema>;
